import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Phone, Clock, MapPin } from 'lucide-react'
import ClickablePhone from './ClickablePhone'
import { CTA_CONTACT_INFO } from '../constants/stats'

const CTASection = () => {
  const contactInfo = CTA_CONTACT_INFO

  // Маппинг иконок
  const iconMap = {
    Phone,
    Clock,
    MapPin
  }

  return (
    <section className="section-padding bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Готовы защитить ваш автомобиль от коррозии?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Запишитесь на антикоррозийную обработку прямо сейчас и получите
              скидку 10% на первый визит. Наши специалисты проведут бесплатную
              диагностику и подберут оптимальный вариант защиты.
            </p>

            {/* Benefits */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <span className="text-white/90">Бесплатная диагностика</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <span className="text-white/90">Скидка 10% на первый визит</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <span className="text-white/90">Гарантия качества до 5 лет</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="group bg-accent-500 hover:bg-accent-600 text-white font-semibold py-4 px-8 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Записаться сейчас</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <Link
                to="/services"
                className="group border-2 border-white/30 text-white font-semibold py-4 px-8 rounded-lg text-lg hover:bg-white/10 transition-all duration-200 backdrop-blur-sm flex items-center justify-center space-x-2"
              >
                <span>Узнать цены</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
          </motion.div>

          {/* Right side - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-accent-500 rounded-full flex items-center justify-center flex-shrink-0">
                    {React.createElement(iconMap[info.icon as keyof typeof iconMap], {
                      className: "w-6 h-6 text-white"
                    })}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {info.title}
                    </h3>
                    <div className="text-xl font-bold text-accent-400 mb-1">
                      {info.title === 'Телефон' ? (
                        <ClickablePhone
                          phone={info.value}
                        />
                      ) : (
                        info.value
                      )}
                    </div>
                    <p className="text-white/70 text-sm">
                      {info.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Special Offer Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-gradient-to-r from-accent-500 to-accent-600 rounded-xl p-6 text-white text-center"
            >
              <div className="text-2xl font-bold mb-2">🎉 Специальное предложение!</div>
              <div className="text-lg mb-2">Скидка 10% на первый визит</div>
              <div className="text-sm text-accent-100">
                Действует до конца месяца
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Не уверены в выборе услуги?
            </h3>
            <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
              Наши специалисты проведут бесплатную консультацию и помогут
              подобрать оптимальный вариант для вашего автомобиля
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default CTASection
